import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Player implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private speed: number = 200;
  private shootCooldown: number = 0;
  private shootDelay: number = 0.15; // Can shoot every 150ms
  
  // Movement boundaries
  private minX: number = 8;
  private maxX: number;
  private minY: number;
  private maxY: number;

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number,
    gameHeight: number,
    playerZoneHeight: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    
    // Set movement boundaries
    this.maxX = gameWidth - this.size.x - 8;
    this.minY = gameHeight - playerZoneHeight;
    this.maxY = gameHeight - this.size.y - 8;
  }

  public update(deltaTime: number): void {
    // Update shoot cooldown
    this.shootCooldown -= deltaTime;
    
    // Apply movement
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Clamp to boundaries
    this.position.x = Math.max(this.minX, Math.min(this.maxX, this.position.x));
    this.position.y = Math.max(this.minY, Math.min(this.maxY, this.position.y));
    
    // Reset velocity (player stops when not pressing keys)
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  public moveLeft(): void {
    this.velocity.x = -this.speed;
  }

  public moveRight(): void {
    this.velocity.x = this.speed;
  }

  public moveUp(): void {
    this.velocity.y = -this.speed;
  }

  public moveDown(): void {
    this.velocity.y = this.speed;
  }

  public shoot(): { x: number; y: number } | null {
    if (this.shootCooldown > 0) return null;
    
    this.shootCooldown = this.shootDelay;
    this.audioManager.playShoot();
    
    return {
      x: this.position.x + this.size.x / 2,
      y: this.position.y
    };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Draw player as a stylized ship
    const pattern = [
      [0,0,0,1,1,0,0,0],
      [0,0,1,1,1,1,0,0],
      [0,1,1,1,1,1,1,0],
      [1,1,1,1,1,1,1,1],
      [1,0,1,1,1,1,0,1],
      [1,0,0,1,1,0,0,1],
      [0,0,0,1,1,0,0,0],
      [0,0,1,0,0,1,0,0]
    ];

    const pixelSize = 2;
    const color = '#00FF00'; // Green player ship

    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - (pattern[row].length * pixelSize) / 2 + col * pixelSize;
          const y = centerY - (pattern.length * pixelSize) / 2 + row * pixelSize;
          this.renderManager.fillRect(x, y, pixelSize, pixelSize, color);
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public reset(): void {
    this.active = true;
    this.shootCooldown = 0;
  }
}